import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sun, Sunrise, Moon, Clock, Leaf, RefreshCw, ChefHat, Utensils,
  X, Check, ArrowLeft, Sparkles, ArrowRight, Smile, HeartHandshake,
  History, Calendar, Star, Shield, Flame, Info, Share2, RotateCcw,
  Home, Dumbbell
} from "lucide-react";
import { Button } from "../components/ui/button";
import WelcomePage from "./WelcomePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// First-time User Welcome/Onboarding Screen
const WelcomeOnboarding = ({ onStart }) => {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 sm:px-8 animate-fade-in-up" data-testid="welcome-onboarding">
      {/* Chef Hat Icon */}
      <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-primary/10 flex items-center justify-center mb-8 sm:mb-10">
        <ChefHat size={40} className="text-primary sm:w-14 sm:h-14" strokeWidth={1.5} />
      </div>
      
      {/* Headline */}
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground text-center mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'serif' }}>
        Aaj khane mein kya banega?
      </h1>
      
      {/* Subtext - Made more prominent */}
      <p className="text-lg sm:text-xl md:text-2xl text-foreground/80 text-center max-w-md mb-2 sm:mb-3 font-medium">
        We'll help you decide.
      </p>
      <p className="text-sm sm:text-base md:text-lg text-muted-foreground text-center max-w-md mb-10 sm:mb-14">
        The more you use the app, the better your suggestions become.
      </p>
      
      {/* Let's Start Button */}
      <Button
        onClick={onStart}
        className="btn-primary px-8 sm:px-12 py-5 sm:py-7 text-lg sm:text-xl rounded-full shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
        data-testid="lets-start-btn"
      >
        <Sparkles size={20} className="mr-2 sm:mr-3 sm:w-6 sm:h-6" />
        Let's Start
      </Button>
    </div>
  );
};

// Profile Setup Screen - Friendly 3-question onboarding
const ProfileSetup = ({ memberId, onComplete, initialData }) => {
  const [step, setStep] = useState(1);
  const [dietary, setDietary] = useState(initialData?.dietary_type || null);
  const [ageGroup, setAgeGroup] = useState(initialData?.age_group_v2 || null);
  const [foodGoal, setFoodGoal] = useState(initialData?.food_goal || null);
  const [basePreference, setBasePreference] = useState(initialData?.base_preference || null);
  const [dinnerStyle, setDinnerStyle] = useState(initialData?.dinner_style || null);
  const [saving, setSaving] = useState(false);

  const dietOptions = [
    { value: "VEGAN", label: "Vegan", desc: "No dairy, no animal products" },
    { value: "VEGETARIAN", label: "Vegetarian", desc: "Pure veg, the way I like it" },
    { value: "EGGETARIAN", label: "Egg lover", desc: "Veg + eggs work for me" },
    { value: "NON_VEG", label: "Non-Veg", desc: "I enjoy everything" },
  ];

  const ageOptions = [
    { value: "under_25", label: "Under 25" },
    { value: "25_35", label: "25 - 35" },
    { value: "35_45", label: "35 - 45" },
    { value: "45_plus", label: "45+" },
  ];

  const foodGoalOptions = [
    { value: "stay_light", label: "Stay light & healthy most days", icon: Leaf },
    { value: "balanced_homestyle", label: "Eat balanced home-style meals", icon: Home },
    { value: "mix_healthy_fun", label: "Mix of healthy + fun meals", icon: Smile },
    { value: "fitness_focused", label: "Focus on fitness (high protein / controlled eating)", icon: Dumbbell },
    { value: "watch_sugar", label: "Watch sugar / manage health (diabetes / low sugar)", icon: Shield },
  ];

  const baseOptions = [
    { value: "roti", label: "Roti person", desc: "Paratha, phulka, roti is life" },
    { value: "rice", label: "Rice person", desc: "Chawal, pulao, biryani — always rice" },
    { value: "balanced", label: "Balanced", desc: "50/50 — I enjoy both equally" },
  ];

  const dinnerOptions = [
    { value: "light", label: "Mostly light dinners", desc: "Khichdi, chilla, soup, simple meals" },
    { value: "balanced", label: "Mostly balanced dinners", desc: "Dal, roti, sabzi — regular home-style" },
    { value: "balanced_indulge", label: "Balanced, but indulge sometimes", desc: "Home-style most days, fun/heavier meals once in a while" },
    { value: "variety_heavy", label: "I enjoy variety, including heavier dinners", desc: "Wraps, paneer, pasta, richer meals more often" },
  ];

  const handleNext = () => {
    if (step === 1 && !dietary) { toast.error("Pick one to continue"); return; }
    if (step === 2 && !ageGroup) { toast.error("Pick your age group"); return; }
    if (step === 2 && !foodGoal) { toast.error("Pick your food goal"); return; }
    if (step === 3 && !basePreference) { toast.error("Pick one to continue"); return; }
    if (step < 4) { setStep(step + 1); return; }
    handleSave();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/member/${memberId}/profile`, {
        dietary_type: dietary,
        age_group_v2: ageGroup,
        food_goal: foodGoal,
        base_preference: basePreference,
        dinner_style: dinnerStyle || "balanced",
      });
      onComplete();
    } catch (err) {
      console.error("Profile save error:", err);
      if (err.name !== 'CanceledError') {
        toast.error("Could not save. Please try again.");
        setSaving(false);
      }
    }
  };

  const progressWidth = `${(step / 4) * 100}%`;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6" data-testid="profile-setup">
      {/* Progress bar */}
      <div className="w-full max-w-md mb-8">
        <div className="h-1 bg-border/40 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500 ease-out" style={{ width: progressWidth }} data-testid="profile-progress-bar" />
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-right">{step} of 4</p>
      </div>

      {/* Step 1: Dietary */}
      {step === 1 && (
        <div className="w-full max-w-md animate-fade-in-up" data-testid="profile-step-1">
          <h2 className="text-2xl font-bold text-foreground mb-2 text-center" style={{ fontFamily: 'serif' }}>I prefer...</h2>
          <p className="text-muted-foreground text-center mb-8 text-sm">Pick what describes you best</p>
          <div className="space-y-3">
            {dietOptions.map((opt) => (
              <button key={opt.value} onClick={() => setDietary(opt.value)}
                className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-all duration-200 ${
                  dietary === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"
                }`} data-testid={`diet-option-${opt.value}`}>
                <span className="font-semibold text-foreground">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Age + Food Goal */}
      {step === 2 && (
        <div className="w-full max-w-md animate-fade-in-up" data-testid="profile-step-2">
          <h2 className="text-2xl font-bold text-foreground mb-2 text-center" style={{ fontFamily: 'serif' }}>A little about you</h2>
          <p className="text-muted-foreground text-center mb-6 text-sm">Helps us personalise your meals</p>
          
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your age group</p>
          <div className="flex gap-2 mb-8">
            {ageOptions.map((opt) => (
              <button key={opt.value} onClick={() => setAgeGroup(opt.value)}
                className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                  ageGroup === opt.value ? "border-primary bg-primary/5" : "border-border/60 bg-white hover:border-primary/30"
                }`} data-testid={`age-option-${opt.value}`}>
                {opt.label}
              </button>
            ))}
          </div>

          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your food goals</p>
          <div className="space-y-2.5">
            {foodGoalOptions.map((opt) => {
              const Icon = opt.icon;
              return (
                <button key={opt.value} onClick={() => setFoodGoal(opt.value)}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3.5 rounded-2xl border-2 transition-all ${
                    foodGoal === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"
                  }`} data-testid={`goal-option-${opt.value}`}>
                  <Icon size={18} className="text-primary/60 flex-shrink-0" strokeWidth={1.5} />
                  <span className="font-medium text-sm text-foreground">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Base Preference */}
      {step === 3 && (
        <div className="w-full max-w-md animate-fade-in-up" data-testid="profile-step-3">
          <h2 className="text-2xl font-bold text-foreground mb-2 text-center" style={{ fontFamily: 'serif' }}>What are you?</h2>
          <p className="text-muted-foreground text-center mb-8 text-sm">This helps us pick the right meals for you</p>
          <div className="space-y-3">
            {baseOptions.map((opt) => (
              <button key={opt.value} onClick={() => setBasePreference(opt.value)}
                className={`w-full text-left px-5 py-5 rounded-2xl border-2 transition-all duration-200 ${
                  basePreference === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"
                }`} data-testid={`base-option-${opt.value}`}>
                <span className="font-semibold text-foreground">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: Dinner Style */}
      {step === 4 && (
        <div className="w-full max-w-md animate-fade-in-up" data-testid="profile-step-4">
          <h2 className="text-2xl font-bold text-foreground mb-2 text-center" style={{ fontFamily: 'serif' }}>How does your dinner look?</h2>
          <p className="text-muted-foreground text-center mb-8 text-sm">Dinner is where most variety happens</p>
          <div className="space-y-3">
            {dinnerOptions.map((opt) => (
              <button key={opt.value} onClick={() => setDinnerStyle(opt.value)}
                className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-all duration-200 ${
                  dinnerStyle === opt.value ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-white hover:border-primary/30"
                }`} data-testid={`dinner-option-${opt.value}`}>
                <span className="font-semibold text-foreground text-sm">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="w-full max-w-md mt-8 flex gap-3">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full px-6 py-5" data-testid="profile-back-btn">
            <ArrowLeft size={18} />
          </Button>
        )}
        <Button onClick={handleNext} disabled={saving}
          className="flex-1 rounded-full py-5 text-base bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
          data-testid="profile-next-btn">
          {saving ? "Saving..." : step === 4 ? "Let's go!" : "Continue"}
          {!saving && <ArrowRight size={18} className="ml-2" />}
        </Button>
      </div>
    </div>
  );
};

// Personalizing transition screen
const PersonalizingScreen = ({ onDone }) => {
  useEffect(() => {
    const timer = setTimeout(onDone, 2500);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 animate-fade-in" data-testid="personalizing-screen">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 animate-pulse">
        <Sparkles size={28} className="text-primary" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2 text-center" style={{ fontFamily: 'serif' }}>
        Personalizing KhanaFix for you...
      </h2>
      <p className="text-sm text-muted-foreground text-center">Hang tight, this takes just a moment</p>
    </div>
  );
};

// Helper to get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "Good Morning", icon: Sunrise, mealFocus: "breakfast" };
  if (hour < 17) return { text: "Good Afternoon", icon: Sun, mealFocus: "lunch" };
  return { text: "Good Evening", icon: Moon, mealFocus: "dinner" };
};

// Helper to get day of week
const getDayOfWeek = () => {
  return new Date().toLocaleDateString('en-US', { weekday: 'long' });
};

// Meal Card Component
const MealCard = ({ meal, showReason, reason, onRefresh, isLoading, size = "default" }) => {
  const mealTypeIcons = {
    Breakfast: Sunrise,
    breakfast: Sunrise,
    Lunch: Sun,
    lunch: Sun,
    Dinner: Moon,
    dinner: Moon
  };
  const Icon = mealTypeIcons[meal?.primary_meal_type] || Utensils;

  if (!meal) {
    return (
      <div 
        className={`meal-card flex items-center justify-center ${size === "large" ? "min-h-[300px]" : "min-h-[200px]"}`}
        data-testid="meal-card-empty"
      >
        <p className="text-muted-foreground">No meal available</p>
      </div>
    );
  }

  return (
    <div 
      className={`meal-card group ${size === "large" ? "" : ""}`}
      data-testid={`meal-card-${meal.primary_meal_type?.toLowerCase()}`}
    >
      <div className="meal-image-container">
        <img 
          src={meal.image_url || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800"}
          alt={meal.meal_name}
          className="meal-image"
          loading="lazy"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="tag tag-meal-type flex items-center gap-1">
            <Icon size={12} strokeWidth={1.5} />
            {meal.primary_meal_type}
          </span>
          {meal.diet_type && (
            <span className={`tag flex items-center gap-1 ${
              meal.diet_type === "Vegan" ? "bg-green-600/90 text-white" :
              meal.diet_type === "Veg" ? "bg-green-500/90 text-white" :
              meal.diet_type === "Egg" ? "bg-amber-500/90 text-white" :
              meal.diet_type === "Non-Veg" ? "bg-red-500/90 text-white" :
              "bg-green-500/90 text-white"
            }`}>
              <span className={`w-2 h-2 rounded-sm ${
                meal.diet_type === "Vegan" ? "bg-green-200" :
                meal.diet_type === "Veg" ? "bg-green-200" :
                meal.diet_type === "Egg" ? "bg-amber-200" :
                "bg-red-200"
              }`} />
              {meal.diet_type}
            </span>
          )}
          {meal.is_gluten_free && (
            <span className="tag bg-teal-500/90 text-white text-[10px]">GF</span>
          )}
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur rounded-full shadow-sm hover:bg-white transition-all duration-300 opacity-0 group-hover:opacity-100"
            data-testid={`refresh-${meal.primary_meal_type?.toLowerCase()}`}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} strokeWidth={1.5} />
          </button>
        )}
      </div>
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="heading-section text-xl md:text-2xl">{meal.meal_name}</h3>
            <p className="text-muted-foreground text-sm mt-0.5">{meal.components}</p>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground shrink-0">
            <Clock size={14} strokeWidth={1.5} />
            <span className="text-sm">{meal.prep_time_min} min</span>
          </div>
        </div>
        {(meal.calories > 0 || meal.protein > 0) && (
          <div className="flex items-center gap-3 mb-2 text-xs text-muted-foreground" data-testid="meal-card-nutrition">
            {meal.calories > 0 && (
              <span className="flex items-center gap-1">
                <Flame size={12} className="text-orange-400" />
                {meal.calories} kcal
              </span>
            )}
            {meal.protein > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[8px] font-bold">P</span>
                {meal.protein}g protein
              </span>
            )}
          </div>
        )}
        <p className="text-body text-sm md:text-base line-clamp-2">{meal.notes}</p>
        {showReason && reason && (
          <p className="mt-4 text-sm text-primary font-medium italic">"{reason}"</p>
        )}
      </div>
    </div>
  );
};
// Meal Detail Page Component
const MealDetailPage = ({ meal, onBack, onMakeToday }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  if (!meal) return null;

  return (
    <div className="min-h-[80vh] flex flex-col animate-fade-in-up" data-testid="meal-detail-page">
      {/* Hero Image */}
      <div className="relative h-64 md:h-80 overflow-hidden rounded-b-3xl">
        <img 
          src={meal.image_url || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800"}
          alt={meal.meal_name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Back Button */}
        <button
          onClick={onBack}
          className="absolute top-6 left-6 p-3 bg-white/90 backdrop-blur rounded-full shadow-lg hover:bg-white transition-all"
          data-testid="detail-back-btn"
        >
          <ArrowLeft size={20} className="text-foreground" />
        </button>

        {/* Make Today floating button */}
        {onMakeToday && (
          <button
            onClick={onMakeToday}
            className="absolute top-6 right-6 flex items-center gap-1.5 px-4 py-2.5 bg-white/90 backdrop-blur rounded-full shadow-lg hover:bg-white transition-all"
            data-testid="make-today-floating-btn"
          >
            <span className="text-sm font-semibold text-foreground">Add for today</span>
          </button>
        )}

        {/* Tags on image */}
        <div className="absolute bottom-6 left-6 flex gap-2">
          {meal.jain_safe && (
            <span className="bg-accent text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
              <Leaf size={14} /> Veg
            </span>
          )}
          <span className="bg-white/20 backdrop-blur text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
            <Clock size={14} /> {meal.prep_time_min} min
          </span>
          <span className="bg-white/20 backdrop-blur text-white text-sm px-3 py-1 rounded-full">
            {meal.complexity}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 md:p-8 max-w-2xl mx-auto w-full">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="heading-display text-3xl md:text-4xl mb-2">{meal.meal_name}</h1>
          <p className="text-primary text-lg font-medium">{meal.components}</p>
        </div>

        {/* Description */}
        <p className="text-body text-center text-lg leading-relaxed mb-8">
          {meal.notes}
        </p>

        {/* Category & Cuisine */}
        <div className="flex justify-center gap-6 text-sm text-muted-foreground mb-8">
          <div className="text-center">
            <p className="font-medium text-foreground">{meal.cuisine}</p>
            <p>Cuisine</p>
          </div>
          <div className="w-px bg-border" />
          <div className="text-center">
            <p className="font-medium text-foreground">{meal.base}</p>
            <p>Base</p>
          </div>
          <div className="w-px bg-border" />
          <div className="text-center">
            <p className="font-medium text-foreground capitalize">{meal.primary_meal_type}</p>
            <p>Meal</p>
          </div>
        </div>

        {/* Nutrition Info */}
        {(meal.calories > 0 || meal.protein > 0) && (
          <div className="bg-muted/30 rounded-2xl p-5 mb-8" data-testid="detail-nutrition">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Nutrition (per serving)</h3>
            <div className="flex justify-center gap-8">
              {meal.calories > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Flame size={16} className="text-orange-500" />
                    <span className="text-2xl font-bold text-foreground">{meal.calories}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">kcal</p>
                </div>
              )}
              {meal.protein > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">P</span>
                    <span className="text-2xl font-bold text-foreground">{meal.protein}g</span>
                  </div>
                  <p className="text-xs text-muted-foreground">protein</p>
                </div>
              )}
              {meal.diabetic_score > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="text-2xl font-bold text-foreground">{meal.diabetic_score}</span>
                    <span className="text-sm text-muted-foreground">/5</span>
                  </div>
                  <p className="text-xs text-muted-foreground">diabetic score</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ingredients */}
        {meal.ingredients && meal.ingredients.length > 0 && (
          <div className="mb-8" data-testid="detail-ingredients">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Ingredients</h3>
            <div className="bg-white rounded-xl border border-border/40 divide-y divide-border/20">
              {meal.ingredients.map((item, i) => (
                <div key={i} className="px-4 py-2.5 flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-sm text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Steps */}
        {meal.steps && meal.steps.length > 0 && (
          <div className="mb-10" data-testid="detail-steps">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">How to Make</h3>
            <div className="space-y-3">
              {meal.steps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                  <p className="text-sm text-foreground leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="flex justify-center">
          <Button
            className="btn-primary px-8 py-6 text-base rounded-full"
            onClick={onMakeToday}
            data-testid="make-today-btn"
          >
            <Check size={18} className="mr-2" />
            Make This Today
          </Button>
        </div>
      </div>
    </div>
  );
};

// Meal Card Component (simplified - no swipe)
const SwipeCard = ({ meal, onLike, onSkip, isActive, style, reason, isPersonalized, isNewDish, isExploration }) => {
  if (!meal) return null;

  return (
    <div
      className={`absolute inset-0 transition-all duration-300 ${isActive ? 'z-10' : 'z-0'}`}
      style={{
        transform: isActive 
          ? 'scale(1)' 
          : `scale(${0.95 - (style?.index || 0) * 0.02}) translateY(${(style?.index || 0) * 8}px)`,
        opacity: isActive ? 1 : 0.7 - (style?.index || 0) * 0.2,
      }}
      data-testid={`swipe-card-${meal.meal_id || meal.id}`}
    >
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden h-full border border-border/40 flex flex-col">
        {/* New Dish Badge */}
        {isNewDish && (
          <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
            <Sparkles size={12} /> NEW
          </div>
        )}
        
        {/* Exploration Badge */}
        {isExploration && !isNewDish && (
          <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
            <Star size={12} /> Try this!
          </div>
        )}
        
        {/* Image - Takes more space on mobile */}
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <img 
            src={meal.image_url || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800"}
            alt={meal.meal_name}
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 sm:p-6">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {meal.jain_safe && (
                <span className="bg-accent text-white text-xs px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
                  <Leaf size={10} className="sm:w-3 sm:h-3" /> Veg
                </span>
              )}
              <span className="bg-white/20 text-white text-xs px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
                <Clock size={10} className="sm:w-3 sm:h-3" /> {meal.prep_time_min} min
              </span>
              <span className="bg-white/20 text-white text-xs px-2 py-0.5 sm:py-1 rounded-full">
                {meal.complexity}
              </span>
              {meal.meal_nature && (
                <span className={`text-white text-xs px-2 py-0.5 sm:py-1 rounded-full font-medium ${
                  meal.meal_nature === "Light" ? "bg-green-500/70" :
                  meal.meal_nature === "Indulgent" ? "bg-orange-500/70" : "bg-blue-500/50"
                }`}>
                  {meal.meal_nature}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Content - Compact on mobile */}
        <div className="p-3 sm:p-5 flex flex-col gap-1 sm:gap-2">
          <h3 className="font-semibold text-lg sm:text-xl leading-tight">{meal.meal_name}</h3>
          <p className="text-primary text-xs sm:text-sm font-medium">{meal.components}</p>
          
          {/* Personalized Reason */}
          {reason && (
            <p className="text-xs sm:text-sm flex items-center gap-1">
              {isPersonalized ? (
                <>
                  <Sparkles size={12} className="text-primary" />
                  <span className="text-primary font-medium italic">"{reason}"</span>
                </>
              ) : (
                <span className="text-muted-foreground italic">"{reason}"</span>
              )}
            </p>
          )}
          
          <p className="text-muted-foreground text-xs sm:text-sm line-clamp-1 sm:line-clamp-2">{meal.notes}</p>
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-muted-foreground">
            <span className="bg-muted px-2 py-0.5 rounded-full">{meal.cuisine}</span>
            <span className="bg-muted px-2 py-0.5 rounded-full">{meal.base}</span>
            {meal.calories > 0 && (
              <span className="bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Flame size={10} /> {meal.calories} kcal
              </span>
            )}
            {meal.protein > 0 && (
              <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{meal.protein}g protein</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Meal Type Selector Component
// Home Meal Card - Square card with X/✓ or Change button
const HomeMealCard = ({ mealType, meal, isSelected, selectedMeal, onTick, onSkip, onInfo, onChange, loading }) => {
  const mealTypeInfo = {
    breakfast: { label: 'Breakfast', icon: Sunrise },
    lunch: { label: 'Lunch', icon: Sun },
    dinner: { label: 'Dinner', icon: Moon }
  };
  const info = mealTypeInfo[mealType];
  const displayMeal = isSelected ? selectedMeal : meal;

  if (loading) {
    return (
      <div className="mb-6" data-testid={`home-card-${mealType}-loading`}>
        <h2 className="text-lg font-bold text-foreground mb-2.5" style={{ fontFamily: 'Playfair Display, serif' }}>{info.label}</h2>
        <div className="bg-white rounded-2xl border border-border/40 overflow-hidden animate-pulse">
          <div className="w-full aspect-square bg-muted/40 flex items-center justify-center">
            <RefreshCw size={24} className="text-muted-foreground animate-spin" />
          </div>
          <div className="p-4"><div className="h-5 bg-muted/40 rounded w-2/3 mb-2" /><div className="h-4 bg-muted/40 rounded w-1/2" /></div>
        </div>
      </div>
    );
  }

  if (!displayMeal) {
    return (
      <div className="mb-6" data-testid={`home-card-${mealType}-empty`}>
        <h2 className="text-lg font-bold text-foreground mb-2.5" style={{ fontFamily: 'Playfair Display, serif' }}>{info.label}</h2>
        <div className="bg-white rounded-2xl border border-border/40 p-8 flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <info.icon size={24} strokeWidth={1.5} />
          <p className="text-sm">No {info.label.toLowerCase()} options available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid={`home-card-${mealType}`}>
      <h2 className="text-lg font-bold text-foreground mb-2.5" style={{ fontFamily: 'Playfair Display, serif' }}>{info.label}</h2>

      {isSelected ? (
        /* ---- SELECTED STATE: Same full image card with green border + Change ---- */
        <>
          <div className="bg-white rounded-2xl border-2 border-green-500/40 overflow-hidden relative" data-testid={`home-meal-card-${mealType}`}>
            {/* Selected badge */}
            <div className="absolute top-3 left-3 z-10 w-8 h-8 rounded-full bg-green-600 flex items-center justify-center shadow-md" data-testid={`selected-badge-${mealType}`}>
              <Check size={18} className="text-white" strokeWidth={3} />
            </div>
            <button
              onClick={() => onInfo(displayMeal)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center hover:bg-black/60 transition-all"
              data-testid={`info-${mealType}-btn`}
            >
              <Info size={16} className="text-white" />
            </button>

            <img
              src={displayMeal.image_url || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800"}
              alt={displayMeal.meal_name}
              className="w-full aspect-[4/3] object-cover cursor-pointer"
              loading="lazy"
              onClick={() => onInfo(displayMeal)}
              data-testid={`image-${mealType}`}
            />

            <div className="px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-foreground truncate flex-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {displayMeal.meal_name}
                </h3>
                {displayMeal.diet_type && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0 ${
                    displayMeal.diet_type === "Vegan" ? "bg-green-100 text-green-700" :
                    displayMeal.diet_type === "Veg" ? "bg-green-100 text-green-700" :
                    displayMeal.diet_type === "Egg" ? "bg-amber-100 text-amber-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-sm ${
                      displayMeal.diet_type === "Veg" || displayMeal.diet_type === "Vegan" ? "bg-green-600" :
                      displayMeal.diet_type === "Egg" ? "bg-amber-600" : "bg-red-600"
                    }`} />
                    {displayMeal.diet_type}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                {displayMeal.calories > 0 && (
                  <span className="flex items-center gap-1"><Flame size={12} className="text-orange-500" />{displayMeal.calories} kcal</span>
                )}
                {displayMeal.protein > 0 && (
                  <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[8px] font-bold">P</span>{displayMeal.protein}g</span>
                )}
                {displayMeal.prep_time_min > 0 && (
                  <span className="flex items-center gap-1"><Clock size={12} />{displayMeal.prep_time_min} min</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-5 mt-3">
            <button
              onClick={onChange}
              className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary/30 bg-white text-primary font-medium text-sm hover:border-primary hover:bg-primary/5 transition-all"
              data-testid={`change-${mealType}-btn`}
            >
              <RefreshCw size={16} />Change
            </button>
          </div>
        </>
      ) : (
        /* ---- UNSELECTED STATE: Full image card with tick/skip ---- */
        <>
          <div className="bg-white rounded-2xl border-2 border-border/40 overflow-hidden relative">
            <button
              onClick={() => onInfo(displayMeal)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center hover:bg-black/60 transition-all"
              data-testid={`info-${mealType}-btn`}
            >
              <Info size={16} className="text-white" />
            </button>

            <img
              src={displayMeal.image_url || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800"}
              alt={displayMeal.meal_name}
              className="w-full aspect-[4/3] object-cover cursor-pointer"
              loading="lazy"
              onClick={() => onInfo(displayMeal)}
              data-testid={`image-${mealType}`}
            />

            <div className="px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-foreground truncate flex-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {displayMeal.meal_name}
                </h3>
                {displayMeal.diet_type && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0 ${
                    displayMeal.diet_type === "Vegan" ? "bg-green-100 text-green-700" :
                    displayMeal.diet_type === "Veg" ? "bg-green-100 text-green-700" :
                    displayMeal.diet_type === "Egg" ? "bg-amber-100 text-amber-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-sm ${
                      displayMeal.diet_type === "Veg" || displayMeal.diet_type === "Vegan" ? "bg-green-600" :
                      displayMeal.diet_type === "Egg" ? "bg-amber-600" : "bg-red-600"
                    }`} />
                    {displayMeal.diet_type}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                {displayMeal.calories > 0 && (
                  <span className="flex items-center gap-1"><Flame size={12} className="text-orange-500" />{displayMeal.calories} kcal</span>
                )}
                {displayMeal.protein > 0 && (
                  <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[8px] font-bold">P</span>{displayMeal.protein}g</span>
                )}
                {displayMeal.prep_time_min > 0 && (
                  <span className="flex items-center gap-1"><Clock size={12} />{displayMeal.prep_time_min} min</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-5 mt-3">
            <button
              onClick={onSkip}
              className="w-12 h-12 rounded-full border-2 border-border/60 bg-white flex items-center justify-center hover:border-muted-foreground/40 transition-all"
              data-testid={`skip-${mealType}-btn`}
              title="Show more"
            >
              <X size={20} className="text-muted-foreground" />
            </button>
            <button
              onClick={() => onTick(displayMeal)}
              className="w-14 h-14 rounded-full border-2 border-green-300 bg-green-50 flex items-center justify-center hover:border-green-500 hover:bg-green-100 transition-all"
              data-testid={`tick-${mealType}-btn`}
            >
              <Check size={24} className="text-green-600" strokeWidth={2.5} />
            </button>
          </div>
          <div className="flex justify-center gap-5 mt-1.5 text-[10px] text-muted-foreground/70 font-medium">
            <span className="w-12 text-center">Show more</span>
            <span className="w-14 text-center">Pick for today</span>
          </div>
        </>
      )}
    </div>
  );
};

// Meal Display Section - read-only, shows selected meals at top
const MealDisplaySection = ({ todaysMeals, activeTab, dayLabel }) => {
  const hasAny = todaysMeals?.breakfast || todaysMeals?.lunch || todaysMeals?.dinner;
  if (!hasAny) return null;

  const slots = [
    { key: "breakfast", label: "Breakfast", emoji: "\u{1F305}" },
    { key: "lunch", label: "Lunch", emoji: "\u{2600}\u{FE0F}" },
    { key: "dinner", label: "Dinner", emoji: "\u{1F319}" },
  ];

  const displaySlots = [
    { key: "breakfast", label: "Breakfast", icon: Sunrise },
    { key: "lunch", label: "Lunch", icon: Sun },
    { key: "dinner", label: "Dinner", icon: Moon },
  ];

  const totalCal = (todaysMeals.breakfast?.calories || 0) + (todaysMeals.lunch?.calories || 0) + (todaysMeals.dinner?.calories || 0);
  const totalPro = (todaysMeals.breakfast?.protein || 0) + (todaysMeals.lunch?.protein || 0) + (todaysMeals.dinner?.protein || 0);

  const handleShare = () => {
    const label = dayLabel || "Today's";
    let text = `\u{1F37D} ${label} Meal Plan\n\n`;
    slots.forEach(({ key, label: slotLabel, emoji }) => {
      const meal = todaysMeals[key];
      if (meal) {
        text += `${emoji} ${slotLabel}: ${meal.meal_name}`;
        if (meal.calories > 0) text += ` (${meal.calories} kcal)`;
        text += `\n`;
      }
    });
    if (totalCal > 0) text += `\nTotal: ~${totalCal} kcal | ~${totalPro}g protein`;
    text += `\n\nPlanned with KhanaFix`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="bg-white rounded-2xl border border-border/40 overflow-hidden mb-6" data-testid="meal-display-section">
      <div className="bg-green-50 px-4 py-2.5 flex items-center justify-between border-b border-green-100">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-green-600" />
          <span className="text-xs font-semibold text-green-700">{dayLabel || "Today's"} Meal Plan</span>
        </div>
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-[11px] font-semibold transition-all"
          data-testid="share-whatsapp-btn"
        >
          <Share2 size={12} />
          Share
        </button>
      </div>
      <div className="divide-y divide-border/20">
        {displaySlots.map(({ key, label, icon: SlotIcon }) => {
          const meal = todaysMeals[key];
          if (meal) {
            return (
              <div key={key} className="flex items-center gap-3 px-4 py-2.5" data-testid={`display-${key}`}>
                <img src={meal.image_url || meal.meal_image} alt={meal.meal_name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{meal.meal_name}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                    <span>{label}</span>
                    {meal.calories > 0 && (
                      <span className="flex items-center gap-0.5 text-orange-500 font-medium"><Flame size={8} />{meal.calories} kcal</span>
                    )}
                    {meal.protein > 0 && (
                      <span className="text-blue-500 font-medium">{meal.protein}g protein</span>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={key} className="flex items-center gap-3 px-4 py-2.5 opacity-40" data-testid={`display-${key}-empty`}>
              <div className="w-10 h-10 rounded-lg bg-muted/40 flex items-center justify-center shrink-0">
                <SlotIcon size={16} className="text-muted-foreground" strokeWidth={1.5} />
              </div>
              <span className="text-xs text-muted-foreground">{label} — not yet picked</span>
            </div>
          );
        })}
      </div>
      {totalCal > 0 && (
        <div className="px-4 py-2 bg-muted/20 flex items-center justify-center gap-4 text-xs border-t border-border/20" data-testid="display-totals">
          <span className="flex items-center gap-1 text-orange-600 font-medium"><Flame size={11} />~{totalCal} kcal</span>
          <span className="text-border">|</span>
          <span className="text-blue-600 font-medium">~{totalPro}g protein</span>
        </div>
      )}
    </div>
  );
};

// Helper to get tab labels for 3-day planning
const getDayTabLabels = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date(today);
  dayAfter.setDate(dayAfter.getDate() + 2);

  const formatShort = (d) => {
    const day = d.getDate();
    const suffix = [11,12,13].includes(day) ? 'th' : {1:'st',2:'nd',3:'rd'}[day%10] || 'th';
    const month = d.toLocaleDateString('en-IN', { month: 'short' });
    return `${day}${suffix} ${month}`;
  };

  return [
    { key: 'today', label: 'Today', dateStr: today.toISOString().split('T')[0] },
    { key: 'tomorrow', label: formatShort(tomorrow), dateStr: tomorrow.toISOString().split('T')[0] },
    { key: 'dayAfter', label: formatShort(dayAfter), dateStr: dayAfter.toISOString().split('T')[0] },
  ];
};

// Day Tabs Component
const DayTabs = ({ activeTab, onTabChange }) => {
  const tabs = getDayTabLabels();
  return (
    <div className="flex gap-2 pb-2 mb-4" data-testid="day-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onTabChange(tab.key)}
          className={`flex-1 whitespace-nowrap rounded-full py-2 text-xs font-semibold transition-colors text-center ${
            activeTab === tab.key
              ? 'bg-primary text-white shadow-md shadow-primary/30'
              : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'
          }`}
          data-testid={`tab-${tab.key}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// Future Day Placeholder (Tomorrow / Day After)
const FutureDayPlaceholder = ({ dayLabel }) => {
  return (
    <div className="text-center py-16 px-6" data-testid="future-day-placeholder">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
        <Calendar size={28} className="text-primary" strokeWidth={1.5} />
      </div>
      <h3 className="text-lg font-bold text-foreground mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Plan for {dayLabel}
      </h3>
      <p className="text-sm text-muted-foreground max-w-xs mx-auto">
        Recommendations will appear here in Phase 2. For now, focus on today's meals!
      </p>
    </div>
  );
};

// History Tab Placeholder
const HistoryTabContent = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const userEmail = localStorage.getItem("userEmail");

  useEffect(() => {
    const fetchHistory = async () => {
      if (!userEmail) { setLoading(false); return; }
      try {
        const response = await axios.get(`${API}/history/${userEmail}`);
        // Show only last 7 days
        setHistory((response.data || []).slice(0, 7));
      } catch (e) {
        console.error("Error fetching history:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [userEmail]);

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="history-tab-loading">
        <RefreshCw size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-16 px-6" data-testid="history-tab-empty">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <History size={28} className="text-primary" strokeWidth={1.5} />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          No history yet
        </h3>
        <p className="text-sm text-muted-foreground">Your past meal selections will appear here.</p>
      </div>
    );
  }

  return (
    <div data-testid="history-tab-content">
      <h3 className="text-lg font-bold text-foreground mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
        Recent Meals
      </h3>
      {history.map((dayGroup, idx) => (
        <div key={dayGroup.date || idx} className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary/70 mb-2">
            {dayGroup.date === new Date().toISOString().split('T')[0] ? 'Today' :
             dayGroup.date === (() => { const d = new Date(); d.setDate(d.getDate()-1); return d.toISOString().split('T')[0]; })() ? 'Yesterday' :
             new Date(dayGroup.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
          </p>
          {(dayGroup.meals || []).map((meal, mIdx) => (
            <div key={mIdx} className="flex items-center gap-3 py-3 border-b border-border/30 last:border-0">
              <img
                src={meal.meal_image || `${API}/images/${(meal.meal_name || '').toLowerCase().replace(/\s+/g, '_')}.jpg`}
                alt={meal.meal_name}
                className="w-11 h-11 rounded-xl object-cover bg-muted"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wide text-primary/70">{meal.meal_type}</p>
                <p className="text-sm font-semibold text-foreground truncate">{meal.meal_name}</p>
              </div>
            </div>
          ))}
        </div>
      ))}
      <Link to="/history" className="block text-center text-sm font-semibold text-primary py-3 mt-2" data-testid="see-all-history">
        See all history &rarr;
      </Link>
    </div>
  );
};

// Home Page Content - shows greeting, banner, meal display, and 3 meal cards
const HomeContent = ({ userName, todaysMeals, topPicks, loadingStates, onTick, onSkip, onInfo, onChange, activeTab, onTabChange }) => {
  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;
  const tabs = getDayTabLabels();

  useEffect(() => { window.scrollTo(0, 0); }, [activeTab]);

  // Get heading text based on active tab
  const getTabHeading = () => {
    if (activeTab === 'today') return { sub: getDayOfWeek(), main: `${greeting.text}${userName ? `, ${userName}` : ''}` };
    const tabInfo = tabs.find(t => t.key === activeTab);
    return { sub: `Planning for ${tabInfo?.label || ''}`, main: 'What about next?' };
  };

  const heading = getTabHeading();

  return (
    <div className="px-5 py-6 pb-24 max-w-md mx-auto" data-testid="home-content">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5 animate-fade-in-up">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <GreetingIcon size={20} className="text-primary" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[2px] text-primary/70">{heading.sub}</p>
          <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Playfair Display, serif' }}>
            {heading.main}
          </h1>
        </div>
      </div>

      {/* Day Tabs */}
      <DayTabs activeTab={activeTab} onTabChange={onTabChange} />

      {/* Today Tab Content */}
      {activeTab === 'today' && (
        <>
          {/* Banner */}
          <div className="flex items-center gap-3 bg-white border border-border/40 rounded-2xl px-4 py-3 mb-5 animate-fade-in-up" data-testid="reco-banner">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles size={14} className="text-primary" />
            </div>
            <p className="text-xs text-foreground/70"><span className="font-semibold text-foreground">Today's picks, just for you.</span></p>
          </div>

          {/* Meal Display Section */}
          <MealDisplaySection todaysMeals={todaysMeals} activeTab={activeTab} dayLabel="Today's" />

          {/* 3 Meal Cards */}
          <div className="animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            {['breakfast', 'lunch', 'dinner'].map((type) => (
              <HomeMealCard
                key={type}
                mealType={type}
                meal={topPicks[type]}
                isSelected={!!todaysMeals[type]}
                selectedMeal={todaysMeals[type]}
                onTick={(meal) => onTick(type, meal)}
                onSkip={() => onSkip(type)}
                onInfo={(m) => onInfo(m, type)}
                onChange={() => onChange(type)}
                loading={loadingStates[type]}
              />
            ))}
          </div>

          {/* Plan next day CTA */}
          <div className="flex justify-center pt-2 pb-4">
            <button
              onClick={() => onTabChange('tomorrow')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
              data-testid="plan-tomorrow-btn"
            >
              <Calendar size={16} />Plan your next day
              <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}

      {/* Tomorrow / Day After Tab Content */}
      {(activeTab === 'tomorrow' || activeTab === 'dayAfter') && (
        <>
          {/* Banner */}
          <div className="flex items-center gap-3 bg-white border border-border/40 rounded-2xl px-4 py-3 mb-5 animate-fade-in-up" data-testid="reco-banner">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles size={14} className="text-primary" />
            </div>
            <p className="text-xs text-foreground/70"><span className="font-semibold text-foreground">Picks for {tabs.find(t => t.key === activeTab)?.label}.</span></p>
          </div>

          {/* Meal Display Section */}
          <MealDisplaySection todaysMeals={todaysMeals} activeTab={activeTab} dayLabel={tabs.find(t => t.key === activeTab)?.label + "'s"} />

          {/* 3 Meal Cards */}
          <div className="animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            {['breakfast', 'lunch', 'dinner'].map((type) => (
              <HomeMealCard
                key={type}
                mealType={type}
                meal={topPicks[type]}
                isSelected={!!todaysMeals[type]}
                selectedMeal={todaysMeals[type]}
                onTick={(meal) => onTick(type, meal)}
                onSkip={() => onSkip(type)}
                onInfo={(m) => onInfo(m, type)}
                onChange={() => onChange(type)}
                loading={loadingStates[type]}
              />
            ))}
          </div>

          {/* Plan next day CTA - only on tomorrow tab */}
          {activeTab === 'tomorrow' && (
            <div className="flex justify-center pt-2 pb-4">
              <button
                onClick={() => onTabChange('dayAfter')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                data-testid="plan-dayafter-btn"
              >
                <Calendar size={16} />Plan day after
                <ArrowRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MealTypeSelector = ({ onSelect, userName, todaysMeals }) => {
  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;
  
  // Learning banner state - show for first 7 days, dismissible
  const [showLearningBanner, setShowLearningBanner] = useState(() => {
    // Check if user dismissed the banner
    const dismissed = localStorage.getItem("learningBannerDismissed");
    if (dismissed === "true") return false;
    
    // Check how long user has been using the app
    const signupDate = localStorage.getItem("userSignupDate");
    if (!signupDate) {
      // First time - set signup date
      localStorage.setItem("userSignupDate", new Date().toISOString());
      return true;
    }
    
    // Calculate days since signup
    const daysSinceSignup = Math.floor(
      (new Date() - new Date(signupDate)) / (1000 * 60 * 60 * 24)
    );
    
    // Show banner only for first 7 days
    return daysSinceSignup <= 7;
  });

  const dismissLearningBanner = () => {
    setShowLearningBanner(false);
    localStorage.setItem("learningBannerDismissed", "true");
  };

  const mealTypes = [
    { 
      type: 'breakfast', 
      label: 'Breakfast', 
      hindi: 'नाश्ता',
      icon: Sunrise, 
      color: 'bg-amber-50 hover:bg-amber-100 border-amber-200',
      selectedColor: 'bg-amber-100 border-amber-300',
      iconColor: 'text-amber-600'
    },
    { 
      type: 'lunch', 
      label: 'Lunch', 
      hindi: 'दोपहर का खाना',
      icon: Sun, 
      color: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
      selectedColor: 'bg-orange-100 border-orange-300',
      iconColor: 'text-orange-600'
    },
    { 
      type: 'dinner', 
      label: 'Dinner', 
      hindi: 'रात का खाना',
      icon: Moon, 
      color: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200',
      selectedColor: 'bg-indigo-100 border-indigo-300',
      iconColor: 'text-indigo-600'
    },
  ];

  const hasAnySelection = Object.values(todaysMeals || {}).some(m => m);
  const allSelected = todaysMeals?.breakfast && todaysMeals?.lunch && todaysMeals?.dinner;

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 sm:px-6" data-testid="meal-type-selector">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12 animate-fade-in-up">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 sm:mb-6">
          <GreetingIcon size={28} className="text-primary sm:w-8 sm:h-8" strokeWidth={1.5} />
        </div>
        <p className="text-caption mb-2 text-xs sm:text-sm">{getDayOfWeek()}</p>
        <h1 className="heading-display text-2xl sm:text-3xl md:text-4xl mb-3 sm:mb-4">
          {greeting.text}{userName ? `, ${userName}` : ''}
        </h1>
        <p className="text-body text-base sm:text-lg max-w-md mx-auto">
          {allSelected
            ? "Your day is sorted. Enjoy your meals!"
            : hasAnySelection 
              ? "Here's your plan for today. Tap to change any meal."
              : "What meal are you thinking about?"
          }
        </p>
      </div>

      {/* Plan Complete Card - Shows when all 3 meals selected */}
      {allSelected && (
        <div className="mb-6 sm:mb-8 w-full max-w-md animate-fade-in-up" data-testid="plan-complete-card">
          <div className="bg-white rounded-2xl shadow-lg border border-accent/30 overflow-hidden">
            <div className="bg-accent/10 px-4 py-3 flex items-center justify-center gap-2">
              <Check size={18} className="text-accent" />
              <span className="font-semibold text-sm text-accent">Today's Plan Complete</span>
            </div>
            <div className="divide-y divide-border/20">
              {[
                { key: "breakfast", label: "Breakfast", icon: Sunrise, iconColor: "text-amber-600" },
                { key: "lunch", label: "Lunch", icon: Sun, iconColor: "text-orange-600" },
                { key: "dinner", label: "Dinner", icon: Moon, iconColor: "text-indigo-600" }
              ].map(({ key, label, icon: MealIcon, iconColor }) => {
                const meal = todaysMeals[key];
                return (
                  <div key={key} className="flex items-center gap-3 px-4 py-3" data-testid={`plan-complete-${key}`}>
                    <img 
                      src={meal.image_url || meal.meal_image} 
                      alt={meal.meal_name}
                      className="w-12 h-12 rounded-lg object-cover shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">{meal.meal_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex items-center gap-1">
                          <MealIcon size={11} className={iconColor} />
                          <span className="text-xs text-muted-foreground">{label}</span>
                        </div>
                        {meal.calories > 0 && (
                          <span className="text-[10px] text-orange-500 flex items-center gap-0.5">
                            <Flame size={9} /> {meal.calories} kcal
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Daily Total */}
            {(() => {
              const totalCal = (todaysMeals.breakfast?.calories || 0) + (todaysMeals.lunch?.calories || 0) + (todaysMeals.dinner?.calories || 0);
              const totalPro = (todaysMeals.breakfast?.protein || 0) + (todaysMeals.lunch?.protein || 0) + (todaysMeals.dinner?.protein || 0);
              return totalCal > 0 ? (
                <div className="px-4 py-2.5 bg-muted/30 flex items-center justify-center gap-4 text-xs" data-testid="plan-complete-totals">
                  <span className="flex items-center gap-1 text-orange-600 font-medium">
                    <Flame size={12} /> ~{totalCal} kcal
                  </span>
                  <span className="text-border">|</span>
                  <span className="text-blue-600 font-medium">~{totalPro}g protein</span>
                </div>
              ) : null;
            })()}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-3">Tap any meal below to change it</p>
        </div>
      )}

      {/* Today's Plan Summary pills - Only when partially selected */}
      {hasAnySelection && !allSelected && (
        <div className="mb-6 sm:mb-8 text-center animate-fade-in-up w-full max-w-3xl" data-testid="todays-plan">
          <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">Today's Plan</p>
          <div className="flex items-center justify-center gap-2 flex-wrap px-2">
            {Object.entries(todaysMeals).map(([type, meal]) => {
              if (!meal) return null;
              return (
                <span 
                  key={type}
                  className="inline-flex items-center gap-1.5 sm:gap-2 bg-white border border-primary/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm shadow-sm"
                >
                  {type === 'breakfast' && <Sunrise size={12} className="text-amber-600 sm:w-3.5 sm:h-3.5" />}
                  {type === 'lunch' && <Sun size={12} className="text-orange-600 sm:w-3.5 sm:h-3.5" />}
                  {type === 'dinner' && <Moon size={12} className="text-indigo-600 sm:w-3.5 sm:h-3.5" />}
                  <span className="font-medium">{meal.meal_name}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Learning Banner - Shows for first 7 days */}
      {showLearningBanner && (
        <div className="w-full max-w-md mb-6 sm:mb-8 animate-fade-in-up px-2" style={{ animationDelay: '200ms' }}>
          <div className="relative bg-primary/10 rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <Sparkles size={16} className="text-primary sm:w-5 sm:h-5" />
            </div>
            <p className="text-xs sm:text-sm text-foreground/80 pr-6">
              <span className="font-medium text-foreground">Every swipe teaches me your taste.</span>
              {" "}The more you use, the better I get!
            </p>
            <button
              onClick={dismissLearningBanner}
              className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-primary/10 transition-colors"
              aria-label="Dismiss"
            >
              <X size={14} className="text-muted-foreground sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Meal Type Cards - Stack on mobile, row on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 w-full max-w-3xl px-2 sm:px-0">
        {mealTypes.map((item, index) => {
          const Icon = item.icon;
          const selectedMeal = todaysMeals?.[item.type];
          const hasSelection = !!selectedMeal;
          
          return (
            <button
              key={item.type}
              onClick={() => onSelect(item.type)}
              className={`${hasSelection ? item.selectedColor : item.color} border-2 rounded-2xl sm:rounded-3xl p-4 sm:p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg animate-fade-in-up group relative overflow-hidden`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`select-${item.type}`}
            >
              {/* Selected indicator */}
              {hasSelection && (
                <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-accent flex items-center justify-center">
                    <Check size={12} className="text-white sm:w-3.5 sm:h-3.5" strokeWidth={3} />
                  </div>
                </div>
              )}
              
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-sm group-hover:scale-110 transition-transform`}>
                <Icon size={20} className={`${item.iconColor} sm:w-6 sm:h-6`} strokeWidth={1.5} />
              </div>
              <h3 className="heading-section text-base sm:text-lg mb-0.5 sm:mb-1">{item.label}</h3>
              
              {hasSelection ? (
                <div className="mt-1 sm:mt-2" data-testid={`selected-meal-${item.type}`}>
                  <p className="text-foreground font-medium text-xs sm:text-sm">{selectedMeal.meal_name}</p>
                  <p className="text-muted-foreground text-xs hidden sm:block">{selectedMeal.components}</p>
                </div>
              ) : (
                <p className="text-muted-foreground text-xs mt-0.5 sm:mt-1">{item.hindi}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Swipe Interface Component
const SwipeInterface = ({ mealType, onBack, onMealSelected, excludeMealIds = [] }) => {
  const [allMeals, setAllMeals] = useState([]);
  const [displayedMeals, setDisplayedMeals] = useState([]);
  const [mealReasons, setMealReasons] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [showGetMore, setShowGetMore] = useState(false);
  const [showNoMore, setShowNoMore] = useState(false);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const mealsPerBatch = 5;
  const maxRefreshes = 1;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const mealTypeInfo = {
    breakfast: { label: 'Breakfast', icon: Sunrise, color: 'text-amber-600' },
    lunch: { label: 'Lunch', icon: Sun, color: 'text-orange-600' },
    dinner: { label: 'Dinner', icon: Moon, color: 'text-indigo-600' }
  };

  const info = mealTypeInfo[mealType];
  const Icon = info.icon;

  // Helper to save user action
  const saveAction = async (actionType, meal) => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail || !meal) return;

    try {
      await axios.post(`${API}/actions`, {
        user_email: userEmail,
        action_type: actionType,
        meal_id: meal.meal_id || meal.id,
        meal_name: meal.meal_name,
        meal_type: mealType,
        cuisine: meal.cuisine || "",
        base: meal.base || "",
        complexity: meal.complexity || ""
      });
    } catch (error) {
      console.error("Error saving action:", error);
    }
  };

  // Track meals as seen
  const trackSeenMeals = async (meals) => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail || !meals.length) return;

    try {
      await axios.post(`${API}/seen`, {
        user_email: userEmail,
        meal_ids: meals.map(m => m.meal_id || m.id)
      });
    } catch (error) {
      console.error("Error tracking seen meals:", error);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchMeals = async () => {
      try {
        setLoading(true);
        
        const userEmail = localStorage.getItem("userEmail");
        const excludeParam = excludeMealIds.length > 0 ? excludeMealIds.join(',') : undefined;
        const response = await axios.get(`${API}/meals/personalized`, {
          params: {
            meal_type: mealType,
            user_email: userEmail || undefined,
            exclude_meal_ids: excludeParam
          }
        });
        
        if (cancelled) return;

        const { meals: personalizedMeals, user_has_history, has_new_dishes } = response.data;
        setIsPersonalized(user_has_history || has_new_dishes);
        
        const mealsOnly = personalizedMeals.map(pm => pm.meal);
        const reasons = {};
        personalizedMeals.forEach(pm => {
          reasons[pm.meal.meal_id || pm.meal.id] = {
            reason: pm.reason,
            isPersonalized: pm.is_personalized,
            isNewDish: pm.is_new_dish,
            isExploration: pm.is_exploration,
            score: pm.score
          };
        });
        
        setAllMeals(mealsOnly);
        setMealReasons(reasons);
        const displayMeals = mealsOnly.slice(0, mealsPerBatch);
        setDisplayedMeals(displayMeals);
        
        trackSeenMeals(displayMeals);
      } catch (e) {
        console.error("Error fetching meals:", e);
        if (!cancelled) toast.error("Couldn't load meals");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMeals();
    return () => { cancelled = true; };
  }, [mealType]);

  const handleLike = async () => {
    const likedMeal = displayedMeals[currentIndex];
    
    // Save LIKE action
    await saveAction("like", likedMeal);
    
    setSelectedMeal(likedMeal);
    setShowDetail(true);
  };

  const handleSkip = async () => {
    const skippedMeal = displayedMeals[currentIndex];
    
    // Save as "not_today" — soft signal, not a hard skip
    await saveAction("not_today", skippedMeal);
    
    if (currentIndex < displayedMeals.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      if (refreshCount < maxRefreshes) {
        setShowGetMore(true);
      } else {
        setShowNoMore(true);
      }
    }
  };

  const handleNotToday = async () => {
    const meal = displayedMeals[currentIndex];
    
    // Save NOT_TODAY action (neutral — doesn't penalize)
    await saveAction("not_today", meal);
    
    if (currentIndex < displayedMeals.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      if (refreshCount < maxRefreshes) {
        setShowGetMore(true);
      } else {
        setShowNoMore(true);
      }
    }
  };

  const handleGoBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleGetMoreRecommendations = () => {
    const startIndex = (refreshCount + 1) * mealsPerBatch;
    const nextMeals = allMeals.slice(startIndex, startIndex + mealsPerBatch);
    
    if (nextMeals.length === 0) {
      setShowGetMore(false);
      setShowNoMore(true);
      return;
    }

    setDisplayedMeals(nextMeals);
    setCurrentIndex(0);
    setRefreshCount(prev => prev + 1);
    setShowGetMore(false);
    
    // Track these new meals as seen
    trackSeenMeals(nextMeals);
    
    toast.success(`Here are ${nextMeals.length} more options!`, {
      icon: <Sparkles size={18} className="text-primary" />
    });
  };

  const handleBackFromDetail = () => {
    setShowDetail(false);
    setSelectedMeal(null);
  };

  const handleMakeToday = async () => {
    if (selectedMeal) {
      // Save SELECT action (strongest signal)
      await saveAction("select", selectedMeal);
      
      onMealSelected(mealType, selectedMeal);
    }
  };

  const remainingRefreshes = maxRefreshes - refreshCount;

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center" data-testid="swipe-loading">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 animate-pulse">
          <Icon size={32} className={info.color} strokeWidth={1.5} />
        </div>
        <p className="text-muted-foreground">Loading {info.label.toLowerCase()} options...</p>
      </div>
    );
  }

  // Show detail page when a meal is liked
  if (showDetail && selectedMeal) {
    return (
      <MealDetailPage 
        meal={selectedMeal}
        onBack={handleBackFromDetail}
        onMakeToday={handleMakeToday}
      />
    );
  }

  // Show "Get More Recommendations" screen after skipping all 5
  if (showGetMore) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 animate-fade-in-up" data-testid="get-more-screen">
        <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-6">
          <RefreshCw size={36} className="text-secondary-foreground" strokeWidth={1.5} />
        </div>
        <h2 className="heading-section text-2xl text-center mb-3">Not what you're looking for?</h2>
        <p className="text-body text-center max-w-sm mb-2">
          No worries! Here are 5 more {info.label.toLowerCase()} options for you.
        </p>
        <p className="text-sm text-muted-foreground mb-8">
          This is the last batch
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            className="rounded-full px-8"
            onClick={onBack}
            data-testid="back-to-meals-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Meals
          </Button>
          <Button
            className="btn-primary px-8"
            onClick={handleGetMoreRecommendations}
            data-testid="get-more-btn"
          >
            <Sparkles size={18} className="mr-2" />
            Show More Options
          </Button>
        </div>
      </div>
    );
  }

  // Show "No More Options" screen - polite regret after 10 skips
  if (showNoMore) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 animate-fade-in-up" data-testid="no-more-screen">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
          <Utensils size={36} className="text-muted-foreground" strokeWidth={1.5} />
        </div>
        <h2 className="heading-section text-2xl text-center mb-3">We're sorry!</h2>
        <p className="text-body text-center max-w-sm mb-4">
          We couldn't find a {info.label.toLowerCase()} that suits your taste today.
        </p>
        <p className="text-sm text-muted-foreground mb-8">
          The more you use the app, the better we'll understand your preferences.
        </p>
        <Button
          variant="outline"
          className="rounded-full px-8"
          onClick={onBack}
          data-testid="back-to-meals-btn"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Meals
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center px-4 sm:px-6 py-4 sm:py-6" data-testid="swipe-interface">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4 w-full max-w-md">
        <button
          onClick={onBack}
          className="flex items-center gap-1 sm:gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
          <span className="text-sm sm:text-base">Back</span>
        </button>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Icon size={18} className={info.color} />
          <span className="font-medium text-sm sm:text-base">{info.label}</span>
          {isPersonalized && (
            <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              <Sparkles size={10} />
              For you
            </span>
          )}
        </div>
        <div className="text-xs sm:text-sm text-muted-foreground">
          {currentIndex + 1} / {displayedMeals.length}
        </div>
      </div>

      {/* Swipe Area - Properly centered */}
      <div 
        className="relative flex-1 w-full max-w-md flex items-start justify-center"
        style={{ maxHeight: '480px', minHeight: '380px' }}
      >
        {displayedMeals.map((meal, index) => {
          const mealId = meal.meal_id || meal.id;
          const mealInfo = mealReasons[mealId] || {};
          return (
            <SwipeCard
              key={meal.id}
              meal={meal}
              isActive={index === currentIndex}
              onLike={handleLike}
              onSkip={handleSkip}
              style={{ index: index - currentIndex }}
              reason={mealInfo.reason}
              isPersonalized={mealInfo.isPersonalized}
              isNewDish={mealInfo.isNewDish}
              isExploration={mealInfo.isExploration}
            />
          );
        })}
      </div>

      {/* Action Buttons - Show More and Pick */}
      <div className="flex items-center justify-center gap-6 sm:gap-8 mt-4 sm:mt-6">
        <button
          onClick={handleSkip}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white border-2 border-border/60 flex items-center justify-center shadow-lg hover:scale-110 hover:border-muted-foreground/40 transition-all"
          data-testid="skip-btn"
          title="Show more"
        >
          <X size={20} className="text-muted-foreground sm:w-6 sm:h-6" />
        </button>
        <button
          onClick={handleLike}
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30 hover:scale-110 transition-all"
          data-testid="like-btn"
          title="Pick for today"
        >
          <Check size={24} className="text-white sm:w-7 sm:h-7" strokeWidth={3} />
        </button>
      </div>

      {/* Label hint */}
      <div className="flex justify-center gap-6 mt-2 text-[10px] text-muted-foreground/70 font-medium">
        <span className="w-12 text-center">Show more</span>
        <span className="w-14 text-center">Pick for today</span>
      </div>

      {/* Undo button - pill below actions */}
      <div className="flex justify-center mt-3">
        {currentIndex > 0 ? (
          <button
            onClick={handleGoBack}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-amber-200 bg-amber-50/50 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-all"
            data-testid="undo-btn"
          >
            <RotateCcw size={13} />Go back to previous
          </button>
        ) : (
          <p className="text-center text-xs text-muted-foreground/60 py-2">Skip or select your meal</p>
        )}
      </div>
    </div>
  );
};

// Home Page with Tinder-style flow
const HomePage = ({ onBottomBarChange }) => {
  const location = useLocation();
  const [userName, setUserName] = useState(() => localStorage.getItem("userName") || "");
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("userEmail") || "");
  const [selectedMealType, setSelectedMealType] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("userName") && !!localStorage.getItem("userEmail"));
  const [activeTab, setActiveTab] = useState('today');
  const [showWelcomeOnboarding, setShowWelcomeOnboarding] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showPersonalizing, setShowPersonalizing] = useState(false);
  const [memberId, setMemberId] = useState(() => localStorage.getItem("memberId") || "");
  const [preferencesAlreadyDone, setPreferencesAlreadyDone] = useState(false);

  // Multi-day planned meals keyed by date string (e.g. "2026-04-09")
  const emptyDay = { breakfast: null, lunch: null, dinner: null };
  const tabs = getDayTabLabels();
  const getDateForTab = useCallback((tab) => {
    const t = tabs.find(t => t.key === tab);
    return t?.dateStr;
  }, []);

  const [plannedMeals, setPlannedMeals] = useState(() => {
    const saved = localStorage.getItem("mealPlans");
    const plans = saved ? JSON.parse(saved) : {};
    // Migrate old todaysMeals if present
    const oldMeals = localStorage.getItem("todaysMeals");
    const oldDate = localStorage.getItem("todaysMealsDate");
    if (oldMeals && oldDate) {
      const todayStr = new Date().toISOString().split('T')[0];
      const oldDateObj = new Date(oldDate);
      const oldDateStr = oldDateObj.toISOString().split('T')[0];
      if (oldDateStr === todayStr && !plans[todayStr]) {
        plans[todayStr] = JSON.parse(oldMeals);
      }
      localStorage.removeItem("todaysMeals");
      localStorage.removeItem("todaysMealsDate");
    }
    // Clean up past dates (before today)
    const todayStr = new Date().toISOString().split('T')[0];
    Object.keys(plans).forEach(d => { if (d < todayStr) delete plans[d]; });
    return plans;
  });

  // Helper to get meals for current active tab
  const activeDate = getDateForTab(activeTab);
  const activeMeals = plannedMeals[activeDate] || emptyDay;

  // Persist to localStorage whenever plannedMeals changes
  useEffect(() => {
    localStorage.setItem("mealPlans", JSON.stringify(plannedMeals));
  }, [plannedMeals]);

  // Enrich any cached meals that are missing full details (calories, protein, etc.)
  const enrichedRef = useRef(false);
  useEffect(() => {
    if (enrichedRef.current) return;
    const enrichMeals = async () => {
      const incomplete = [];
      for (const [date, dayMeals] of Object.entries(plannedMeals)) {
        for (const [type, meal] of Object.entries(dayMeals)) {
          if (meal && meal.meal_id && meal.calories === undefined) {
            incomplete.push({ date, type, meal_id: meal.meal_id });
          }
        }
      }
      if (incomplete.length === 0) return;
      enrichedRef.current = true;
      const results = await Promise.all(
        incomplete.map(({ meal_id }) => axios.get(`${API}/meals/${meal_id}`).catch(() => null))
      );
      setPlannedMeals(prev => {
        const updated = { ...prev };
        for (let i = 0; i < incomplete.length; i++) {
          const { date, type } = incomplete[i];
          const fullMeal = results[i]?.data;
          if (fullMeal && updated[date]?.[type]) {
            updated[date] = { ...updated[date], [type]: { ...updated[date][type], ...fullMeal } };
          }
        }
        return updated;
      });
    };
    enrichMeals();
  }, [plannedMeals]);

  // Flag: restore from history must complete before fetching recommendations
  const historyRestored = useRef(false);

  // Restore planned meals from history on login
  useEffect(() => {
    if (!userEmail || !isLoggedIn) return;
    const restoreFromHistory = async () => {
      try {
        const res = await axios.get(`${API}/history/${userEmail}`);
        const historyDays = res.data;
        if (!historyDays || historyDays.length === 0) {
          historyRestored.current = true;
          return;
        }

        const tabs = getDayTabLabels();
        const planningDates = tabs.map(t => t.dateStr).filter(Boolean);
        const currentPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
        let changed = false;

        // Collect meal_ids that need full details
        const mealIdsToFetch = [];
        for (const dayEntry of historyDays) {
          const date = dayEntry.date;
          if (!planningDates.includes(date)) continue;
          for (const meal of dayEntry.meals) {
            const type = meal.meal_type;
            if (!currentPlans[date]) currentPlans[date] = { breakfast: null, lunch: null, dinner: null };
            if (!currentPlans[date][type] && meal.meal_id) {
              mealIdsToFetch.push({ date, type, meal });
            }
          }
        }

        if (mealIdsToFetch.length > 0) {
          // Fetch full meal details for all restored meals
          const detailPromises = mealIdsToFetch.map(({ meal }) =>
            axios.get(`${API}/meals/${meal.meal_id}`).catch(() => null)
          );
          const detailResults = await Promise.all(detailPromises);

          for (let i = 0; i < mealIdsToFetch.length; i++) {
            const { date, type, meal } = mealIdsToFetch[i];
            const fullMeal = detailResults[i]?.data;
            currentPlans[date][type] = fullMeal
              ? { ...fullMeal, image_url: fullMeal.image_url || meal.meal_image }
              : { meal_id: meal.meal_id, meal_name: meal.meal_name, image_url: meal.meal_image };
            changed = true;
          }

          // Save to localStorage FIRST so fetchPicksForDate reads the correct data
          localStorage.setItem("mealPlans", JSON.stringify(currentPlans));
          setPlannedMeals(currentPlans);
        }
      } catch (e) {
        // Silently fail
      } finally {
        historyRestored.current = true;
      }
    };
    restoreFromHistory();
  }, [userEmail, isLoggedIn]);

  // Reset selectedMealType when navigating to home via logo/nav
  useEffect(() => {
    if (location.pathname === "/" && location.key !== "default") {
      setSelectedMealType(null);
    }
  }, [location]);

  const handleEnter = (name, email, userData) => {
    setUserName(name);
    setUserEmail(email);
    setIsLoggedIn(true);

    // Store member_id from login response
    if (userData?.member_id) {
      setMemberId(userData.member_id);
      localStorage.setItem("memberId", userData.member_id);
    }

    // If user already completed preferences, skip all onboarding
    if (userData?.preferences_completed) {
      setPreferencesAlreadyDone(true);
      localStorage.setItem("hasSeenWelcome", "true");
      return;
    }

    // New or incomplete user - show onboarding flow
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");
    if (!hasSeenWelcome) {
      setShowWelcomeOnboarding(true);
    } else {
      setShowProfileSetup(true);
    }
  };

  const handleWelcomeComplete = () => {
    localStorage.setItem("hasSeenWelcome", "true");
    setShowWelcomeOnboarding(false);
    // After welcome, show profile setup only if not already completed
    if (!preferencesAlreadyDone) {
      setShowProfileSetup(true);
    }
  };

  const handleProfileComplete = () => {
    setShowProfileSetup(false);
    setShowPersonalizing(true);
  };

  const handlePersonalizingDone = useCallback(() => {
    setShowPersonalizing(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userId");
    localStorage.removeItem("memberId");
    localStorage.removeItem("mealPlans");
    setUserName("");
    setUserEmail("");
    setIsLoggedIn(false);
    setMemberId("");
    setSelectedMealType(null);
    setShowWelcomeOnboarding(false);
    setShowProfileSetup(false);
    setShowPersonalizing(false);
    setPlannedMeals({});
  };

  const handleMealSelected = async (mealType, meal) => {
    // Update planned meals for the active date
    const dateStr = activeDate;
    if (!dateStr) return;
    setPlannedMeals(prev => ({
      ...prev,
      [dateStr]: { ...(prev[dateStr] || emptyDay), [mealType]: meal }
    }));
    setSelectedMealType(null);
    setDetailMeal(null);
    setDetailMealType(null);

    // Save to history in backend
    const email = localStorage.getItem("userEmail");
    if (email) {
      try {
        await axios.post(`${API}/history`, {
          user_email: email,
          meal_type: mealType,
          meal_id: meal.meal_id || meal.id,
          meal_name: meal.meal_name,
          meal_image: meal.image_url,
          date: dateStr
        });
      } catch (error) {
        console.error("Error saving to history:", error);
      }
    }

    // Dynamic toast based on which day
    const dayLabel = activeTab === 'today' ? "today's" : tabs.find(t => t.key === activeTab)?.label + "'s";
    toast.success(`${meal.meal_name} added to ${dayLabel} plan!`, {
      icon: <Check className="text-accent" size={18} />
    });
  };

  // Date-keyed top picks and loading states
  const [topPicksByDate, setTopPicksByDate] = useState({});
  const [loadingByDate, setLoadingByDate] = useState({});
  const fetchedDates = useRef(new Set());
  const topPicksByDateRef = useRef({});
  useEffect(() => { topPicksByDateRef.current = topPicksByDate; }, [topPicksByDate]);

  const fetchPicksForDate = useCallback(async (dateStr, skipHistoryWait) => {
    if (!dateStr || fetchedDates.current.has(dateStr)) return;

    // Wait for history restore to complete before fetching (unless skipped)
    if (!skipHistoryWait) {
      let attempts = 0;
      while (!historyRestored.current && attempts < 20) {
        await new Promise(r => setTimeout(r, 100));
        attempts++;
      }
    }

    // Re-check after restore (another effect might have fetched this date)
    if (fetchedDates.current.has(dateStr)) return;
    fetchedDates.current.add(dateStr);

    const email = localStorage.getItem("userEmail");
    const savedPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
    const dayMeals = savedPlans[dateStr] || {};
    const types = ['breakfast', 'lunch', 'dinner'];

    // Skip already-selected meal types
    const toFetch = types.filter(t => !dayMeals[t]);
    types.forEach(t => {
      if (dayMeals[t]) setLoadingByDate(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr] || {}), [t]: false } }));
    });

    if (toFetch.length === 0) return;

    // Collect meal IDs from OTHER days (selected + previously fetched top picks) to exclude
    const excludeIds = new Set();
    Object.entries(savedPlans).forEach(([d, dayPlan]) => {
      if (d !== dateStr) {
        Object.values(dayPlan || {}).forEach(m => {
          if (m) excludeIds.add(m.meal_id || m.id);
        });
      }
    });
    // Also exclude top picks already shown on other dates (use ref for current value)
    Object.entries(topPicksByDateRef.current).forEach(([d, picks]) => {
      if (d !== dateStr) {
        Object.values(picks || {}).forEach(m => {
          if (m) excludeIds.add(m.meal_id || m.id);
        });
      }
    });

    // Fetch sequentially so each type excludes what was picked for previous types on this day
    const sameDayExcludeIds = new Set([...excludeIds]);
    Object.values(dayMeals || {}).forEach(m => {
      if (m) sameDayExcludeIds.add(m.meal_id || m.id);
    });

    for (const type of toFetch) {
      setLoadingByDate(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr] || {}), [type]: true } }));
      try {
        const sameDayExcludeParam = sameDayExcludeIds.size > 0 ? [...sameDayExcludeIds].join(',') : undefined;
        const res = await axios.get(`${API}/meals/personalized`, {
          params: { meal_type: type, user_email: email || undefined, exclude_meal_ids: sameDayExcludeParam }
        });
        const meals = res.data.meals.map(pm => pm.meal);
        const pick = meals.find(m => !sameDayExcludeIds.has(m.meal_id || m.id)) || meals[0];
        if (pick) {
          sameDayExcludeIds.add(pick.meal_id || pick.id);
          setTopPicksByDate(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr] || {}), [type]: pick } }));
        }
      } catch (e) { /* silent */ }
      setLoadingByDate(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr] || {}), [type]: false } }));
    }
  }, []);

  // Auto-fetch today on login
  useEffect(() => {
    if (isLoggedIn) {
      const todayStr = new Date().toISOString().split('T')[0];
      fetchPicksForDate(todayStr);
    }
  }, [isLoggedIn, fetchPicksForDate]);

  // Lazy-fetch when user switches to a future day tab
  useEffect(() => {
    if (activeTab !== 'today' && activeTab !== 'history' && activeDate) {
      fetchPicksForDate(activeDate);
    }
  }, [activeTab, activeDate, fetchPicksForDate]);

  // Refresh picks when coming back from profile edit
  useEffect(() => {
    if (location.state?.refreshPicks) {
      // Clear all fetched dates so they re-fetch with new preferences
      fetchedDates.current.clear();
      setTopPicksByDate({});
      topPicksByDateRef.current = {};
      fetchPicksForDate(new Date().toISOString().split('T')[0], true);
      window.history.replaceState({}, '');
    }
  }, [location.state, fetchPicksForDate]);

  // Derived state for the active tab
  const activeTopPicks = topPicksByDate[activeDate] || {};
  const activeLoading = loadingByDate[activeDate] || { breakfast: true, lunch: true, dinner: true };

  // Tick = add displayed meal to plan
  const handleTick = async (mealType, meal) => {
    if (!meal) return;
    const userEmail = localStorage.getItem("userEmail");
    if (userEmail) {
      try {
        await axios.post(`${API}/actions`, {
          user_email: userEmail, action_type: "select",
          meal_id: meal.meal_id || meal.id, meal_name: meal.meal_name,
          meal_type: mealType, cuisine: meal.cuisine || "", base: meal.base || "", complexity: meal.complexity || ""
        });
      } catch (e) { /* silent */ }
    }
    handleMealSelected(mealType, meal);
  };

  // Skip (X) = "not today" soft signal + open selection page
  const handleSkip = async (mealType) => {
    const meal = activeTopPicks[mealType];
    if (meal) {
      const userEmail = localStorage.getItem("userEmail");
      if (userEmail) {
        try {
          await axios.post(`${API}/actions`, {
            user_email: userEmail, action_type: "not_today",
            meal_id: meal.meal_id || meal.id, meal_name: meal.meal_name,
            meal_type: mealType, cuisine: meal.cuisine || "", base: meal.base || "", complexity: meal.complexity || ""
          });
        } catch (e) { /* silent */ }
      }
    }
    setSelectedMealType(mealType);
  };

  // Info = show meal detail from home
  const [detailMeal, setDetailMeal] = useState(null);
  const [detailMealType, setDetailMealType] = useState(null);

  const handleInfo = (meal, mealType) => {
    setDetailMeal(meal);
    setDetailMealType(mealType);
  };

  const handleMakeTodayFromDetail = async () => {
    if (detailMeal && detailMealType) {
      const userEmail = localStorage.getItem("userEmail");
      if (userEmail) {
        try {
          await axios.post(`${API}/actions`, {
            user_email: userEmail, action_type: "select",
            meal_id: detailMeal.meal_id || detailMeal.id, meal_name: detailMeal.meal_name,
            meal_type: detailMealType, cuisine: detailMeal.cuisine || "", base: detailMeal.base || "", complexity: detailMeal.complexity || ""
          });
        } catch (e) { /* silent */ }
      }
      handleMealSelected(detailMealType, detailMeal);
    }
  };

  // Change = open Meal Selection Page for a selected meal type
  const handleChange = (mealType) => {
    setSelectedMealType(mealType);
  };

  // Show welcome page if not logged in
  // Control bottom bar visibility - only show on main dashboard
  const isMainDashboard = isLoggedIn && !showWelcomeOnboarding && !showProfileSetup && !showPersonalizing && !selectedMealType && !detailMeal;
  useEffect(() => {
    if (onBottomBarChange) onBottomBarChange(isMainDashboard);
  }, [isMainDashboard, onBottomBarChange]);
  useEffect(() => {
    return () => { if (onBottomBarChange) onBottomBarChange(false); };
  }, [onBottomBarChange]);

  if (!isLoggedIn) {
    return <WelcomePage onEnter={handleEnter} />;
  }

  // Show welcome onboarding for first-time users
  if (showWelcomeOnboarding) {
    return <WelcomeOnboarding onStart={handleWelcomeComplete} />;
  }

  // Show profile setup
  if (showProfileSetup) {
    return <ProfileSetup memberId={memberId} onComplete={handleProfileComplete} />;
  }

  // Show personalizing transition
  if (showPersonalizing) {
    return <PersonalizingScreen onDone={handlePersonalizingDone} />;
  }

  // Show meal detail from home (i) icon — works for any planning tab
  if (activeTab !== 'history' && detailMeal) {
    return (
      <div className="app-container">
        <MealDetailPage
          meal={detailMeal}
          onBack={() => { setDetailMeal(null); setDetailMealType(null); }}
          onMakeToday={handleMakeTodayFromDetail}
        />
      </div>
    );
  }

  // Meal Selection Page (opened via X or Change) — works for any planning tab
  if (activeTab !== 'history' && selectedMealType) {
    // Collect meal IDs from other types on the same day to exclude from swipe
    const sameDayExcludeIds = [];
    const dayMeals = plannedMeals[activeDate] || {};
    const dayPicks = topPicksByDate[activeDate] || {};
    ['breakfast', 'lunch', 'dinner'].forEach(t => {
      if (t !== selectedMealType) {
        const m = dayMeals[t] || dayPicks[t];
        if (m) sameDayExcludeIds.push(m.meal_id || m.id);
      }
    });

    return (
      <div className="app-container">
        <SwipeInterface 
          mealType={selectedMealType}
          onBack={() => setSelectedMealType(null)}
          onMealSelected={handleMealSelected}
          excludeMealIds={sameDayExcludeIds}
        />
      </div>
    );
  }

  return (
    <div className="app-container">
      <HomeContent
        userName={userName}
        todaysMeals={activeMeals}
        topPicks={activeTopPicks}
        loadingStates={activeLoading}
        onTick={handleTick}
        onSkip={handleSkip}
        onInfo={handleInfo}
        onChange={handleChange}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default HomePage;
export { ProfileSetup, PersonalizingScreen, MealCard };
